import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';

import { isCurrentLocationPublic } from '../utils/public-route.util';
import { AuthService } from '../services/auth.service';
import { NC_SESSION_AUTH_HEADER } from './nc-session-auth.interceptor';

const SKIP_REFRESH_HEADER = 'X-Skip-Refresh';

function isAnonymousNcSessionExchange(url: string): boolean {
  return url.toLowerCase().includes('/api/nc/session/exchange');
}

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  if (req.headers.has(NC_SESSION_AUTH_HEADER) || isAnonymousNcSessionExchange(req.url)) {
    return next(req);
  }

  const auth = inject(AuthService);
  const token = auth.token();

  if (!token) return next(req);

  const isRefreshOrRevoke =
    req.url.includes('/api/Auth/refresh-token') || req.url.includes('/api/Auth/revoke-token');
  const isRetry = req.headers.has(SKIP_REFRESH_HEADER);

  const sendRequest = () => {
    const currentToken = auth.token();
    if (!currentToken) {
      return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
    }

    return next(
      req.clone({
        setHeaders: { Authorization: `Bearer ${currentToken}` }
      })
    );
  };

  const sendWith401Recovery = () =>
    sendRequest().pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && !isRefreshOrRevoke && !isRetry) {
          const refresh = auth.getRefreshToken();
          const currentToken = auth.token() ?? token;
          if (!refresh || !currentToken) {
            if (!isCurrentLocationPublic()) {
              auth.handleExpiredSession();
            }
            return throwError(() => err);
          }

          return auth.refreshToken(currentToken, refresh).pipe(
            switchMap(() => {
              const newToken = auth.token();
              if (!newToken) return throwError(() => err);

              return next(
                req.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                    [SKIP_REFRESH_HEADER]: '1'
                  }
                })
              );
            }),
            catchError(() => {
              if (!isCurrentLocationPublic()) {
                auth.handleExpiredSession();
              }
              return throwError(() => err);
            })
          );
        }

        return throwError(() => err);
      })
    );

  if (isRefreshOrRevoke || isRetry) return sendWith401Recovery();

  return auth.ensureValidAccessToken().pipe(
    switchMap((isValid) => {
      if (!isValid) {
        if (!isCurrentLocationPublic()) {
          auth.handleExpiredSession();
        }
        return throwError(() => new HttpErrorResponse({ status: 401, statusText: 'Unauthorized' }));
      }

      return sendWith401Recovery();
    })
  );
};
